import { indentLess, indentMore } from "@codemirror/commands";
import { Compartment } from "@codemirror/state";
import { EditorView } from "@codemirror/view";
import { designVariables } from "@studio3t/design-variables";
import {
  ButtonSmallSubtleIcon,
  GenericDropdown,
  type GenericDropdownItem,
  Icon,
  IconSVG,
  Tooltip,
} from "@studio3t/generic-components";
import CodeMirror, { type ReactCodeMirrorRef } from "@uiw/react-codemirror";
import { type RefObject, useEffect, useMemo, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";
import { playgroundApi } from "@/api/client";
import {
  buildExtensions,
  CONTEXT_MENU_HEIGHT,
  editorRegionFocusRing,
} from "@/components/JsonViewer";
import { OverlayScroll } from "@/components/OverlayScroll";
import { ErrorStatusButton, jumpToOffset } from "./EditorErrorsPopover";
import { type EditorErrorRow, errorRows } from "./editorErrors";
import { singleLinePlaceholder } from "./editorPlaceholder";
import { relintOnDocSwap, viewOnlyContextItems } from "./JsonDocumentEditor";
import { usePipelineStaging } from "./pipelineStaging";
import { dryRunPayload, dryRunScript } from "./rhaiDryRun";
import {
  reindentScript,
  rhaiHighlightStyle,
  rhaiLanguage,
} from "./rhaiLanguage";
import { rhaiLinter } from "./rhaiLint";
import { useStudioPipeline } from "./StudioPipelineProvider";
import { HERO_TEMPLATES } from "./scriptTemplates";
import { useEditorFocusRequest } from "./useEditorFocusRequest";
import { viewOnlyExtensions } from "./viewOnlyEditor";

// Same StatusBar/editor metrics as InputDocumentEditor (Figma StatusBar
// component, UI-Modules 1660-55289 / Bridge_PipelineCode 13970-93956).
const STATUS_PADDING = 5;
const STATUS_GAP = 4;
const LINE_HEIGHT = 24;
// 33px line-number gutter + 14px fold gutter + 20px lint column — the
// shared buildExtensions sidebar (the lint column rides along even
// without a linter), so the template links line up EXACTLY under the
// placeholder's first character.
const GUTTER_WIDTH = 33 + 14 + 20;
const CONTENT_PAD_TOP = 5;

const IS_MAC =
  typeof navigator !== "undefined" &&
  (navigator.platform ?? "").toUpperCase().includes("MAC");
const KEY = (letter: string) => (IS_MAC ? `⌘${letter}` : `Ctrl+${letter}`);

// The hero templates — shared with the AI Helper (scriptTemplates.ts).
const TEMPLATES = HERO_TEMPLATES;

// The engine compiles the script (POST /transform/validate) — the real
// Rhai parser, so the squiggles agree with what a deploy would reject.
const validateScript = (script: string) => playgroundApi.validate({ script });

// …and runs a clean script against the staged input document (the Run
// button's POST /transform/run), since Rhai only resolves variables and
// functions at run time — `let x = wer` parses fine.
const dryRunTransform = (script: string, payload: string) =>
  dryRunScript(playgroundApi.test, script, payload);

/** What the last lint pass said: its error rows, and whether it has run
 *  on the current (non-empty) document at all — before that, the status
 *  bar shows neither the tick nor a count. */
interface ScriptLint {
  rows: EditorErrorRow[];
  checked: boolean;
}

const UNCHECKED: ScriptLint = { rows: [], checked: false };

// View-level clipboard actions; the hook below binds them to the live ref.
// Copies the main selection to the clipboard and returns it; null when the
// selection is empty. Clipboard write can be denied — a failure is a no-op.
function copyDocSelection(view: EditorView) {
  const sel = view.state.selection.main;
  if (sel.empty) return null;
  navigator.clipboard
    .writeText(view.state.sliceDoc(sel.from, sel.to))
    .catch(() => {});
  return sel;
}

function cutDocSelection(view: EditorView): void {
  const sel = copyDocSelection(view);
  if (!sel) return;
  view.dispatch({ changes: { from: sel.from, to: sel.to } });
  view.focus();
}

async function pasteIntoView(view: EditorView): Promise<void> {
  const text = await navigator.clipboard.readText().catch(() => "");
  if (!text) return;
  view.dispatch(view.state.replaceSelection(text));
  view.focus();
}

// Ref-based CodeMirror commands shared by the status bar and context menu.
function useScriptCommands(cmRef: RefObject<ReactCodeMirrorRef | null>) {
  const withView = (action: (view: EditorView) => void) => {
    const view = cmRef.current?.view;
    if (view) action(view);
  };
  return {
    runCommand: (command: typeof indentMore) =>
      withView((view) => {
        command(view);
        view.focus();
      }),
    copySelection: () => withView(copyDocSelection),
    cutSelection: () => withView(cutDocSelection),
    pasteFromClipboard: () => withView(pasteIntoView),
  };
}

// The context-menu model: clipboard, templates, formatting — the handlers
// close over the live editor ref, so the caller rebuilds per render.
function buildScriptContextItems(options: {
  empty: boolean;
  wrap: boolean;
  hasSelection: boolean;
  setScriptDocument: (value: string) => void;
  commands: ReturnType<typeof useScriptCommands>;
  onToggleWrap: () => void;
}): GenericDropdownItem[] {
  const { empty, wrap, hasSelection, setScriptDocument, commands } = options;
  return [
    [
      {
        id: "cut",
        type: "text",
        value: "cut",
        label: "Cut",
        badgeText: KEY("X"),
        disabled: !hasSelection,
        closeOnSelect: true,
        onClick: commands.cutSelection,
      },
      {
        id: "copy",
        type: "text",
        value: "copy",
        label: "Copy",
        badgeText: KEY("C"),
        disabled: !hasSelection,
        closeOnSelect: true,
        onClick: commands.copySelection,
      },
      {
        id: "paste",
        type: "text",
        value: "paste",
        label: "Paste",
        badgeText: KEY("V"),
        closeOnSelect: true,
        onClick: commands.pasteFromClipboard,
      },
      "SEPARATOR",
      ...TEMPLATES.map((template) => ({
        id: template.id,
        type: "text" as const,
        value: template.id,
        label: template.label,
        closeOnSelect: true,
        onClick: () => setScriptDocument(template.script),
      })),
      "SEPARATOR",
      {
        id: "format-script",
        type: "text",
        value: "format-script",
        label: "Format script",
        disabled: empty,
        closeOnSelect: true,
        onClick: () => commands.runCommand(reindentScript),
      },
      {
        id: "indent-less",
        type: "text",
        value: "indent-less",
        label: "Decrease indent",
        disabled: empty,
        closeOnSelect: true,
        onClick: () => commands.runCommand(indentLess),
      },
      {
        id: "indent-more",
        type: "text",
        value: "indent-more",
        label: "Increase indent",
        disabled: empty,
        closeOnSelect: true,
        onClick: () => commands.runCommand(indentMore),
      },
      {
        id: "word-wrap",
        type: "text",
        value: "word-wrap",
        label: wrap ? "Disable word wrap" : "Enable word wrap",
        disabled: empty,
        closeOnSelect: true,
        onClick: options.onToggleWrap,
      },
    ],
  ];
}

// The zero-point template links (lines 2..4 per Figma), with old
// /studio's palette hint under them — the FULL catalog lives in the ⌘K
// command palette (Studio Mask/Transform entries).
function TemplateEmptyState({ onPick }: { onPick: (script: string) => void }) {
  const theme = useTheme();
  return (
    <EmptyStateOverlay>
      {TEMPLATES.map((template) => (
        <TemplateLink
          key={template.id}
          type="button"
          onClick={() => onPick(template.script)}
        >
          <IconSVG
            icon={Icon.Right}
            size={16}
            colors={{ default: theme.linkColor }}
          />
          {template.label}
        </TemplateLink>
      ))}
      <TemplateHint>Press {KEY("K")} for templates</TemplateHint>
    </EmptyStateOverlay>
  );
}

// The muted hint line under the zero-point links (old /studio's "Press
// ⌘⇧P for templates" — the shortcut is the app palette's now).
const TemplateHint = styled.span`
  color: ${(p) => p.theme.editorLineNumTxt};
  font-size: 12px;
  padding-left: 22px;
`;

// The status bar's validity slot (the JSON editors' ValidJsonIcon twin):
// the engine's verdict on the last lint pass — a tick, or the Errors
// button with the rows. Nothing until the first pass has answered.
function ScriptValidity({
  lint,
  onJump,
}: {
  lint: ScriptLint;
  onJump: (from: number) => void;
}) {
  const theme = useTheme();
  if (lint.rows.length > 0) {
    return (
      <ErrorStatusButton
        rows={lint.rows}
        title="Script errors"
        documentName="transform script"
        onJump={onJump}
        testIdBase="transform-script"
      />
    );
  }
  if (!lint.checked) return null;
  return (
    <StatusIconSlot
      title="Valid script"
      data-testid="transform-script-validity"
    >
      <IconSVG
        icon={Icon.Confirm}
        size={16}
        colors={{ default: theme.semanticMainPrimary }}
      />
    </StatusIconSlot>
  );
}

// Editor/StatusBar with the engine's lint verdict in the validity slot;
// Format here is a whitespace-only reindent (reindentScript) — no full
// Rhai formatter exists.
function ScriptStatusBar({
  empty,
  wrap,
  onToggleWrap,
  onClear,
  commands,
  cursor,
  lint,
  onJump,
}: {
  empty: boolean;
  wrap: boolean;
  onToggleWrap: () => void;
  onClear: () => void;
  commands: ReturnType<typeof useScriptCommands>;
  cursor: { line: number; col: number };
  lint: ScriptLint;
  onJump: (from: number) => void;
}) {
  return (
    <StatusBar>
      <StatusActions>
        <Tooltip text="Format script" side="top">
          <ButtonSmallSubtleIcon
            icon={Icon.FormatQuery}
            onClick={() => commands.runCommand(reindentScript)}
            disabled={empty}
          />
        </Tooltip>
        <Tooltip text="Decrease indent" side="top">
          <ButtonSmallSubtleIcon
            icon={Icon.IndentLeft}
            onClick={() => commands.runCommand(indentLess)}
            disabled={empty}
          />
        </Tooltip>
        <Tooltip text="Increase indent" side="top">
          <ButtonSmallSubtleIcon
            icon={Icon.IndentRight}
            onClick={() => commands.runCommand(indentMore)}
            disabled={empty}
          />
        </Tooltip>
        <Tooltip
          text={wrap ? "Disable word wrap" : "Enable word wrap"}
          side="top"
        >
          <ButtonSmallSubtleIcon
            icon={Icon.WordWrap}
            onClick={onToggleWrap}
            disabled={empty}
          />
        </Tooltip>
        <StatusDivider />
        <Tooltip text="Clear" side="top">
          <ButtonSmallSubtleIcon
            icon={Icon.Clean}
            onClick={onClear}
            disabled={empty}
          />
        </Tooltip>
      </StatusActions>
      {!empty && (
        <StatusRight data-testid="transform-script-status">
          <CursorReadout>
            line {cursor.line}, pos {cursor.col}
          </CursorReadout>
          <ScriptValidity lint={lint} onJump={onJump} />
        </StatusRight>
      )}
    </StatusBar>
  );
}

// TRANSFORM twin of InputDocumentEditor: CodeMirror on the shared Raw
// configuration template but speaking Rhai, with the /studio script
// templates as the zero-point options.
export function TransformScriptEditor() {
  const theme = useTheme();
  const cmRef = useRef<ReactCodeMirrorRef>(null);
  useEditorFocusRequest(cmRef);
  const { scriptDocument, setScriptDocument, inputDocument } =
    usePipelineStaging();
  const [wrapPref, setWrap] = useState(true);
  // The view-only studio always wraps — with nothing to edit, the toggle
  // (and the footer it lives in) has no job.
  const { editsLocked } = useStudioPipeline();
  const wrap = editsLocked || wrapPref;
  const [cursor, setCursor] = useState({ line: 1, col: 1 });
  const [hasSelection, setHasSelection] = useState(false);
  // The engine's verdict on the document, from the linter's last pass
  // (TTTL-225) — the squiggles and gutter dots come from the same pass.
  const [lint, setLint] = useState<ScriptLint>(UNCHECKED);
  const editorAreaRef = useRef<HTMLDivElement>(null);
  const empty = scriptDocument === "";
  // The input document the dry run sees lives in a compartment, so a
  // change to it reaches the linter (which re-lints on its own) without
  // rebuilding the extensions on every input keystroke.
  const [payloadCompartment] = useState(() => new Compartment());

  const extensions = useMemo(
    () => [
      payloadCompartment.of(dryRunPayload.of("")),
      ...buildExtensions(theme, {
        lineWrapping: wrap,
        language: rhaiLanguage,
        highlight: rhaiHighlightStyle,
      }),
      ...viewOnlyExtensions(editsLocked),
      rhaiLinter(
        validateScript,
        (diagnostics, state) =>
          setLint({
            rows: errorRows(state, diagnostics),
            checked: state.doc.length > 0,
          }),
        dryRunTransform,
      ),
      relintOnDocSwap,
      singleLinePlaceholder(theme.editorLineNumTxt),
      EditorView.updateListener.of((u) => {
        if (!u.selectionSet && !u.docChanged) return;
        const main = u.state.selection.main;
        const line = u.state.doc.lineAt(main.head);
        setCursor({ line: line.number, col: main.head - line.from + 1 });
        setHasSelection(!main.empty);
      }),
    ],
    [theme, wrap, editsLocked, payloadCompartment],
  );

  // Keep the compartment on the current input document — also after the
  // editor swaps its extensions (which resets the compartment to the
  // empty payload the array above carries).
  // biome-ignore lint/correctness/useExhaustiveDependencies: extensions re-applies the payload after the swap
  useEffect(() => {
    cmRef.current?.view?.dispatch({
      effects: payloadCompartment.reconfigure(dryRunPayload.of(inputDocument)),
    });
  }, [inputDocument, extensions, payloadCompartment]);

  const commands = useScriptCommands(cmRef);
  const onJump = (from: number) => jumpToOffset(cmRef.current?.view, from);
  const onToggleWrap = () => setWrap((prev) => !prev);
  const contextItems = viewOnlyContextItems(
    buildScriptContextItems({
      empty,
      wrap,
      hasSelection,
      setScriptDocument,
      commands,
      onToggleWrap,
    }),
    editsLocked,
    scriptDocument,
  );

  return (
    <Panel data-testid="transform-script-editor">
      <EditorArea ref={editorAreaRef}>
        <EditorScroll>
          <CodeMirror
            ref={cmRef}
            value={scriptDocument}
            onChange={setScriptDocument}
            extensions={extensions}
            placeholder="Write your transform, or choose a template below"
            // In the zero-point state Tab leaves the editor for the template
            // links below it — there is nothing to indent. With content, Tab
            // indents as usual; Escape then Tab still leaves (CodeMirror's
            // own escape hatch).
            indentWithTab={!empty}
            basicSetup={{
              lineNumbers: true,
              foldGutter: false,
              bracketMatching: true,
              closeBrackets: true,
              // Rhai suggestions (keywords + mstream snippets) come from the
              // language's autocomplete data — same set Monaco registers.
              autocompletion: true,
              highlightActiveLine: false,
              highlightActiveLineGutter: false,
            }}
          />
          {empty && !editsLocked && (
            <TemplateEmptyState onPick={setScriptDocument} />
          )}
        </EditorScroll>
        <GenericDropdown
          asContextMenu={editorAreaRef}
          dropdownItems={contextItems}
          width={{ strategy: "fixed", size: 220 }}
          height={CONTEXT_MENU_HEIGHT}
        />
      </EditorArea>

      {/* The view-only studio has nothing to do in the footer — only
          copying is possible — so it goes. */}
      {!editsLocked && (
        <ScriptStatusBar
          empty={empty}
          wrap={wrap}
          onToggleWrap={onToggleWrap}
          onClear={() => setScriptDocument("")}
          commands={commands}
          cursor={cursor}
          lint={lint}
          onJump={onJump}
        />
      )}
    </Panel>
  );
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${(p) => p.theme.editorBackground};
`;

const EditorArea = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const EditorScroll = styled(OverlayScroll)`
  flex: 1;
  min-height: 0;
  ${editorRegionFocusRing}

  /* Positioned so the zero-point overlay is placed against the scrolled
     content: it scrolls with the document and is clipped by the region,
     instead of hanging out over the status bar and whatever sits below
     when the region is short. */
  [data-overlayscrollbars-contents] {
    position: relative;
    display: flex;
    flex-direction: column;
    min-height: 100%;
  }

  [data-overlayscrollbars-contents] > div {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  .cm-editor {
    flex: 1;
  }
`;

// Template links hang 10px under the placeholder line, flush with the
// code column — 16px links on 10px gaps (Figma 14397-86550), the same
// zero-point recipe as the input editor's sample links: inside the
// scrolled content, so they move and clip with the editor.
const EmptyStateOverlay = styled.div`
  position: absolute;
  top: ${CONTENT_PAD_TOP + LINE_HEIGHT + designVariables.ui.semantic.spacings}px;
  left: ${GUTTER_WIDTH + 4}px;
  display: flex;
  flex-direction: column;
  gap: ${designVariables.ui.semantic.spacings}px;
  pointer-events: none;
`;

const TemplateLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${designVariables.ui.link.gap}px;
  height: 16px;
  padding: 0;
  border: none;
  background: none;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  color: ${(p) => p.theme.linkColor};
  cursor: pointer;
  pointer-events: auto;

  &:hover {
    text-decoration: underline;
  }
`;

const StatusBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${STATUS_GAP}px;
  flex-shrink: 0;
  padding: ${STATUS_PADDING}px;
  background: ${(p) => p.theme.editorStatusBackground};
  border-top: 1px solid ${(p) => p.theme.editorStatusBorder};
`;

const StatusActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${STATUS_GAP}px;
  flex: 1;
  min-width: 0;
`;

const StatusRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${STATUS_GAP}px;
  flex-shrink: 0;
`;

// The 20px slot the tick sits in, centred on the cursor readout's line.
const StatusIconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  height: 20px;
`;

const CursorReadout = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: 1.2;
  color: ${(p) => p.theme.semanticTextLight};
  white-space: nowrap;
`;

const StatusDivider = styled.span`
  width: 8px;
  height: 20px;
  flex-shrink: 0;
  background-image: linear-gradient(
    ${(p) => p.theme.editorStatusBorder},
    ${(p) => p.theme.editorStatusBorder}
  );
  background-size: 1px 100%;
  background-position: center;
  background-repeat: no-repeat;
`;
